"use client";

import { c as _c } from "react/compiler-runtime";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { faCaretLeft, faCaretRight, faInfoCircle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, type FormEvent } from "react";
import { Tooltip } from "react-tooltip";
import type { DisplayTz } from "./meme-calendar.helpers";
import { addMonths, dateFromMintNumber, displayedEonNumberFromIndex, displayedEpochNumberFromIndex, displayedEraNumberFromIndex, displayedPeriodNumberFromIndex, displayedSeasonNumberFromIndex, displayedYearNumberFromIndex, formatFullDate, formatFullDateTime, formatMint, getMintNumberForMintDate, getMonthWeeks, getRangeDatesByZoom, getRangeLabel, getSeasonIndexForDate, getSeasonStartDate, isMintEligibleUtcDay, isSznOneIndex, mintStartInstantUtcForMintDay, printCalendarInvites, SEASONS_PER_EPOCH, SEASONS_PER_ERA, SEASONS_PER_PERIOD, SEASONS_PER_YEAR, SZN1_RANGE, SZN1_SEASON_INDEX, toISO, ymd, ZoomLevel } from "./meme-calendar.helpers";
import { getMintOverrideNoteForUtcDay } from "./meme-calendar.overrides";
import { getHistoricalMintsOnUtcDay } from "./meme-calendar.szn1";

/*
 * MemeCalendar (TypeScript version)
 *
 * This component implements a custom calendar for Next.js projects that
 * follows the “Meme” time system.  All divisions (SZN, Year,
 * Epoch, Period, Era, Eon) accumulate from a fixed starting date
 * of 1 January 2026. Users can zoom between divisions and
 * navigate forwards/backwards. Tailwind classes are prefixed with
 * `tw-` - configure your Tailwind setup accordingly.
 */

function formatMonthYearShort(d: Date): string {
  return `${d.toLocaleString("default", {
    month: "short"
  })} ${d.getUTCFullYear()}`;
}
function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}
function getZoomTitle(zoom: ZoomLevel, seasonIndex: number): string {
  const seasonNumber = displayedSeasonNumberFromIndex(seasonIndex).toLocaleString();
  const yearNumber = displayedYearNumberFromIndex(seasonIndex).toLocaleString();
  const epochNumber = displayedEpochNumberFromIndex(seasonIndex).toLocaleString();
  const periodNumber = displayedPeriodNumberFromIndex(seasonIndex).toLocaleString();
  const eraNumber = displayedEraNumberFromIndex(seasonIndex).toLocaleString();
  const eonNumber = displayedEonNumberFromIndex(seasonIndex).toLocaleString();
  switch (zoom) {
    case "szn":
      return `SZN #${seasonNumber}`;
    case "year":
      return `Year #${yearNumber}`;
    case "epoch":
      return `Epoch #${epochNumber}`;
    case "period":
      return `Period #${periodNumber}`;
    case "era":
      return `Era #${eraNumber}`;
    case "eon":
      return `Eon #${eonNumber}`;
  }
}

// Props types
interface MonthProps {
  readonly date: Date;
  readonly onSelectDay?: (date: Date) => void;
  readonly autoOpenYmd?: string;
  readonly displayTz: DisplayTz;
}
interface SeasonViewProps {
  readonly seasonIndex: number;
  readonly onSelectDay?: (date: Date) => void;
  readonly autoOpenYmd?: string;
  readonly displayTz: DisplayTz;
}
interface YearViewProps {
  readonly seasonIndex: number;
  readonly onSelectSeason: (seasonIndex: number) => void;
  readonly onZoomToSeason: () => void;
}
interface EpochViewProps {
  readonly seasonIndex: number;
  readonly onSelectSeason: (seasonIndex: number) => void;
  readonly onSelectYear: (yearNumber: number) => void;
  readonly onZoomToYear: () => void;
}
interface PeriodViewProps {
  readonly seasonIndex: number;
  readonly onSelectEpoch: (epochNumber: number) => void;
  readonly onZoomToEpoch: () => void;
}
interface EraViewProps {
  readonly seasonIndex: number;
  readonly onSelectPeriod: (periodNumber: number) => void;
  readonly onZoomToPeriod: () => void;
}
interface EonViewProps {
  readonly seasonIndex: number;
  readonly onSelectEra: (eraNumber: number) => void;
  readonly onZoomToEra: () => void;
}

/**
 * Month component - renders a month grid with weekday headers.
 */
function Month(t0) {
  const $ = _c(6);
  const {
    date,
    onSelectDay,
    autoOpenYmd,
    displayTz
  } = t0;
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const monthName = new Date(Date.UTC(year, month, 1)).toLocaleString("default", {
    month: "long"
  });
  const weeks = getMonthWeeks(year, month);
  let t1;
  if ($[0] !== autoOpenYmd) {
    t1 = () => {
      if (!autoOpenYmd) {
        return;
      }
      const el = document.getElementById(`meme-cell-${autoOpenYmd}`);
      if (el) {
        try {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center"
          });
        } catch {}
        const t = setTimeout(() => {
          el.dispatchEvent(new MouseEvent("mouseenter", {
            bubbles: true,
            view: window
          }));
          el.dispatchEvent(new MouseEvent("click", {
            bubbles: true,
            view: window
          }));
        }, 60);
        return () => clearTimeout(t);
      }
    };
    $[0] = autoOpenYmd;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useEffect(t1, [autoOpenYmd, year, month]);
  const t2 = "tw-grid tw-grid-cols-7 tw-text-xs tw-text-center tw-font-medium tw-mt-1";
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(_temp);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = weeks.flat().map((day, idx) => {
    if (day === null) {
      return <div key={`empty-${year}-${month}-${idx}`} className="tw-invisible tw-pointer-events-none" />;
    }
    const cellDateUtcDay = new Date(Date.UTC(year, month, day));
    const col = idx % 7;
    const row = Math.floor(idx / 7);
    let tooltipPlace;
    if (col <= 1) {
      tooltipPlace = "right";
    } else {
      if (row <= 1) {
        tooltipPlace = "bottom";
      } else {
        tooltipPlace = "top";
      }
    }
    const historical = getHistoricalMintsOnUtcDay(cellDateUtcDay);
    const isHistoricalMintDay = historical.length > 0;
    const isScheduledMintDay = isMintEligibleUtcDay(cellDateUtcDay);
    const isMintDay = isHistoricalMintDay || isScheduledMintDay;
    const overrideNote = getMintOverrideNoteForUtcDay(cellDateUtcDay);
    const noteTooltipContent = overrideNote ? escapeHtml(overrideNote).replaceAll("\n", "<br />") : "";
    let mintLabel;
    let mintInstantUtc;
    let mintNumber;
    if (isHistoricalMintDay) {
      const first = historical[0];
      const last = historical[historical.length - 1];
      mintNumber = first.id;
      mintInstantUtc = first.instantUtc;
      mintLabel = historical.length === 1 ? `#${first.id}` : `#${first.id}-#${last.id}`;
    } else {
      if (isScheduledMintDay) {
        mintNumber = getMintNumberForMintDate(cellDateUtcDay);
        mintLabel = formatMint(mintNumber);
        mintInstantUtc = mintStartInstantUtcForMintDay(cellDateUtcDay);
      }
    }
    const isToday = ymd(cellDateUtcDay) === ymd(new Date());
    if (!isMintDay) {
      return <div key={ymd(cellDateUtcDay)} className="tw-py-2 tw-min-h-[2.5rem] tw-flex tw-flex-col tw-items-center tw-justify-start tw-border-b-2 tw-text-gray-400" style={{
        borderColor: "#222222",
        borderBottomStyle: "solid"
      }}><span className={`tw-text-xs tw-rounded-full tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center ${isToday ? "tw-bg-[#20fa59] tw-text-black tw-font-semibold" : "tw-text-gray-400"}`}>{day}</span><span className="tw-text-xs tw-font-medium tw-mt-0.5"> </span></div>;
    }
    let tooltipHtml = "";
    let tooltipClassName = "!tw-bg-[#dcc]";
    if (isHistoricalMintDay) {
      const items = historical.map(_temp2).join(", ");
      tooltipHtml = `<div style="min-width:220px">
              <div style="font-weight:600; margin-bottom:3px; font-size:larger">
                Meme${historical.length > 1 ? "s" : ""} ${items}
              </div>
              <div style="margin-bottom:12px">${formatFullDate(historical[0].instantUtc, displayTz)}</div>
            </div>`;
    } else {
      if (mintInstantUtc) {
        const now = new Date();
        const oneLine = mintInstantUtc.getTime() > now.getTime() ? formatFullDateTime(mintInstantUtc, displayTz) : formatFullDate(mintInstantUtc, displayTz);
        const oneLineDivWithNote = noteTooltipContent ? `<div style="margin-bottom:12px">${oneLine}<br />
                <span style="font-size:11px; color: #666;">*${noteTooltipContent}</span></div>` : `<div style="margin-bottom:12px">${oneLine}</div>`;
        const invites = mintInstantUtc.getTime() > now.getTime() ? printCalendarInvites(mintInstantUtc, mintNumber, "#000") : "";
        tooltipHtml = `
              <div style="min-width:220px">
                <div style="font-weight:600; margin-bottom:3px; font-size:larger">Meme ${mintLabel}</div>
                ${oneLineDivWithNote}
                ${invites}
              </div>`;
        if (mintInstantUtc?.getTime() > now.getTime()) {
          tooltipClassName = "!tw-bg-[#eee]";
        }
      }
    }
    return <button type="button" id={`meme-cell-${ymd(cellDateUtcDay)}`} key={ymd(cellDateUtcDay)} className="tw-bg-transparent tw-py-2 tw-min-h-[2.5rem] tw-flex tw-flex-col tw-items-center tw-justify-start tw-border-none tw-border-b-2 tw-cursor-pointer hover:tw-bg-[#eee] hover:tw-text-black" style={{
      borderColor: "#222222",
      borderBottomStyle: "solid"
    }} data-tooltip-id="meme-tooltip" data-tooltip-html={tooltipHtml} data-tooltip-class-name={tooltipClassName} data-tooltip-place={tooltipPlace} onClick={() => onSelectDay?.(cellDateUtcDay)}><span className={`tw-text-xs tw-rounded-full tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center ${isToday ? "tw-bg-[#20fa59] tw-text-black tw-font-semibold" : ""}`}>{day}</span>{mintLabel && <span className="tw-text-xs tw-font-medium tw-text-blue-600 tw-mt-0.5">{mintLabel}</span>}</button>;
  });
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return <div className="tw-p-2 tw-flex tw-flex-col tw-space-y-1 tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222]"><div className="tw-text-center tw-font-semibold tw-text-sm">{monthName} {year}</div>{t5}</div>;
}

/**
 * SeasonView - displays three consecutive months (a quarter).
 */
function _temp2(h) {
  return `#${h.id}`;
}
function _temp(wd) {
  return <div key={wd} className="tw-p-1 tw-border-b-2" style={{
    borderColor: "#888888",
    borderBottomStyle: "solid"
  }}>{wd}</div>;
}
function SeasonView(t0) {
  const $ = _c(5);
  const {
    seasonIndex,
    onSelectDay,
    autoOpenYmd,
    displayTz
  } = t0;
  let t1;
  if ($[0] !== autoOpenYmd || $[1] !== displayTz || $[2] !== onSelectDay || $[3] !== seasonIndex) {
    const seasonStart = getSeasonStartDate(seasonIndex);
    const months = isSznOneIndex(seasonIndex) ? _temp3() : [0, 1, 2].map(offset => {
      const d = new Date(seasonStart);
      d.setUTCMonth(seasonStart.getUTCMonth() + offset, 1);
      d.setUTCHours(0, 0, 0, 0);
      return d;
    });
    t1 = <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-4 tw-mt-4">{months.map(m_0 => <Month key={toISO(m_0)} date={m_0} onSelectDay={onSelectDay} autoOpenYmd={autoOpenYmd} displayTz={displayTz} />)}</div>;
    $[0] = autoOpenYmd;
    $[1] = displayTz;
    $[2] = onSelectDay;
    $[3] = seasonIndex;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/**
 * YearView - displays only seasons with range labels and drills down to SZN view.
 */
function _temp3() {
  const arr = [];
  for (let m = 5; m <= 11; m++) {
    arr.push(new Date(Date.UTC(2022, m, 1)));
  }
  return arr;
}
function YearView(t0) {
  const $ = _c(47);
  const {
    seasonIndex,
    onSelectSeason,
    onZoomToSeason
  } = t0;
  const yearIndex = Math.floor(seasonIndex / SEASONS_PER_YEAR);
  const firstSeasonIndexOfYear = yearIndex * SEASONS_PER_YEAR;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getSeasonIndexForDate(new Date());
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const currentIdx = t1;
  const displayedYear = displayedYearNumberFromIndex(seasonIndex);
  if (displayedYear === 0) {
    let t10;
    let t11;
    let t12;
    let t13;
    let t14;
    let t15;
    let t16;
    let t17;
    let t2;
    let t3;
    let t4;
    let t5;
    let t6;
    let t7;
    let t8;
    let t9;
    if ($[1] !== onSelectSeason || $[2] !== onZoomToSeason) {
      const start = new Date(SZN1_RANGE.start);
      const end = new Date(SZN1_RANGE.end);
      const isCurrent = currentIdx === SZN1_SEASON_INDEX;
      t17 = "tw-grid tw-grid-cols-1 tw-gap-4 tw-mt-4";
      t11 = "button";
      t12 = SZN1_SEASON_INDEX;
      t13 = "tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-text-black";
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = {
          borderColor: isCurrent ? "#20fa59" : "#222222",
          borderWidth: isCurrent ? "2px" : "1px"
        };
        $[19] = t14;
      } else {
        t14 = $[19];
      }
      t15 = () => {
        onSelectSeason(SZN1_SEASON_INDEX);
        onZoomToSeason();
      };
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <div className="tw-font-semibold">SZN #1</div>;
        $[20] = t16;
      } else {
        t16 = $[20];
      }
      t2 = "tw-text-xs tw-text-gray-500";
      t3 = start.toLocaleString("default", {
        month: "short"
      });
      t4 = " ";
      t5 = start.getUTCFullYear();
      t6 = " -";
      t7 = " ";
      t8 = end.toLocaleString("default", {
        month: "short"
      });
      t9 = " ";
      t10 = end.getUTCFullYear();
      $[1] = onSelectSeason;
      $[2] = onZoomToSeason;
      $[3] = t10;
      $[4] = t11;
      $[5] = t12;
      $[6] = t13;
      $[7] = t14;
      $[8] = t15;
      $[9] = t16;
      $[10] = t17;
      $[11] = t2;
      $[12] = t3;
      $[13] = t4;
      $[14] = t5;
      $[15] = t6;
      $[16] = t7;
      $[17] = t8;
      $[18] = t9;
    } else {
      t10 = $[3];
      t11 = $[4];
      t12 = $[5];
      t13 = $[6];
      t14 = $[7];
      t15 = $[8];
      t16 = $[9];
      t17 = $[10];
      t2 = $[11];
      t3 = $[12];
      t4 = $[13];
      t5 = $[14];
      t6 = $[15];
      t7 = $[16];
      t8 = $[17];
      t9 = $[18];
    }
    let t18;
    if ($[21] !== t10 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
      t18 = <div className={t2}>{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}</div>;
      $[21] = t10;
      $[22] = t2;
      $[23] = t3;
      $[24] = t4;
      $[25] = t5;
      $[26] = t6;
      $[27] = t7;
      $[28] = t8;
      $[29] = t9;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    let t19;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <div className="tw-text-sm tw-mt-1">Memes #1 - #47</div>;
      $[31] = t19;
    } else {
      t19 = $[31];
    }
    let t20;
    if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t14 || $[36] !== t15 || $[37] !== t16 || $[38] !== t18) {
      t20 = <button type={t11} key={t12} className={t13} style={t14} onClick={t15}>{t16}{t18}{t19}</button>;
      $[32] = t11;
      $[33] = t12;
      $[34] = t13;
      $[35] = t14;
      $[36] = t15;
      $[37] = t16;
      $[38] = t18;
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    let t21;
    if ($[40] !== t17 || $[41] !== t20) {
      t21 = <div className={t17}>{t20}</div>;
      $[40] = t17;
      $[41] = t20;
      $[42] = t21;
    } else {
      t21 = $[42];
    }
    return t21;
  }
  let t2;
  if ($[43] !== firstSeasonIndexOfYear || $[44] !== onSelectSeason || $[45] !== onZoomToSeason) {
    const seasons = Array.from({
      length: 4
    }, (_, s) => {
      const sIdx_0 = firstSeasonIndexOfYear + s;
      const start_0 = getSeasonStartDate(sIdx_0);
      const end_0 = addMonths(start_0, 2);
      return {
        sIdx: sIdx_0,
        start: start_0,
        end: end_0,
        label: getRangeLabel(start_0, end_0)
      };
    });
    t2 = <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mt-4">{seasons.map(s_0 => {
        const isCurrent_0 = currentIdx === s_0.sIdx;
        return <button type="button" key={s_0.sIdx} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={{
          borderColor: isCurrent_0 ? "#20fa59" : "#222222",
          borderWidth: isCurrent_0 ? "2px" : "1px"
        }} onClick={() => {
          onSelectSeason(s_0.sIdx);
          onZoomToSeason();
        }}><div className="tw-font-semibold">SZN #{displayedSeasonNumberFromIndex(s_0.sIdx)}</div><div className="tw-text-xs tw-text-gray-500">{s_0.start.toLocaleString("default", {
              month: "short"
            })}{" "}{s_0.start.getUTCFullYear()} -{" "}{s_0.end.toLocaleString("default", {
              month: "short"
            })}{" "}{s_0.end.getUTCFullYear()}</div><div className="tw-text-sm tw-mt-1">{s_0.label}</div></button>;
      })}</div>;
    $[43] = firstSeasonIndexOfYear;
    $[44] = onSelectSeason;
    $[45] = onZoomToSeason;
    $[46] = t2;
  } else {
    t2 = $[46];
  }
  return t2;
}

/**
 * EpochView - years only, one per row, with mint ranges. Click drills into Year view.
 */
function EpochView(t0) {
  const $ = _c(15);
  const {
    seasonIndex,
    onSelectYear,
    onSelectSeason,
    onZoomToYear
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getSeasonIndexForDate(new Date());
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const currentIdx = t1;
  const epochNumber = displayedEpochNumberFromIndex(seasonIndex);
  if (epochNumber === 0) {
    const isCurrent = currentIdx === SZN1_SEASON_INDEX;
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        borderColor: isCurrent ? "#20fa59" : "#222222",
        borderWidth: isCurrent ? "2px" : "1px"
      };
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== onSelectSeason || $[3] !== onSelectYear || $[4] !== onZoomToYear) {
      t3 = () => {
        onSelectSeason(SZN1_SEASON_INDEX);
        onSelectYear(0);
        onZoomToYear();
      };
      $[2] = onSelectSeason;
      $[3] = onSelectYear;
      $[4] = onZoomToYear;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    let t5;
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-font-semibold">Year #0 (2022)</div>;
      t5 = <div className="tw-text-xs tw-text-gray-500">Jun 2022 - Dec 2022</div>;
      t6 = <div className="tw-text-sm tw-mt-1">Memes #1 - #47</div>;
      $[6] = t4;
      $[7] = t5;
      $[8] = t6;
    } else {
      t4 = $[6];
      t5 = $[7];
      t6 = $[8];
    }
    let t7;
    if ($[9] !== t3) {
      t7 = <div className="tw-grid tw-grid-cols-1 tw-gap-4 tw-mt-4"><button type="button" key={SZN1_SEASON_INDEX} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={t2} onClick={t3}>{t4}{t5}{t6}</button></div>;
      $[9] = t3;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  } else {
    const startYear = 2023 + 4 * (epochNumber - 1);
    let t2;
    if ($[11] !== onSelectYear || $[12] !== onZoomToYear || $[13] !== startYear) {
      const years = Array.from({
        length: 4
      }, (_, yearOffset) => {
        const year = startYear + yearOffset;
        const yearSeasonIndex = getSeasonIndexForDate(new Date(Date.UTC(year, 0, 1)));
        const start = new Date(Date.UTC(year, 0, 1));
        const end = new Date(Date.UTC(year, 11, 31));
        const yearNumber = displayedYearNumberFromIndex(yearSeasonIndex);
        return {
          yearNumber,
          start,
          end,
          seasonIndex: yearSeasonIndex,
          label: getRangeLabel(start, end)
        };
      });
      t2 = <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mt-4">{years.map(y => {
          const isCurrent_0 = currentIdx >= y.seasonIndex && currentIdx < y.seasonIndex + SEASONS_PER_YEAR;
          return <button type="button" key={toISO(y.start)} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={{
            borderColor: isCurrent_0 ? "#20fa59" : "#222222",
            borderWidth: isCurrent_0 ? "2px" : "1px"
          }} onClick={() => {
            onSelectYear(y.yearNumber);
            onZoomToYear();
          }}><div className="tw-font-semibold">Year #{y.yearNumber} ({y.start.getUTCFullYear()})</div><div className="tw-text-xs tw-text-gray-500">{y.start.toLocaleString("default", {
                month: "short"
              })}{" "}{y.start.getUTCFullYear()} -{" "}{y.end.toLocaleString("default", {
                month: "short"
              })}{" "}{y.end.getUTCFullYear()}</div><div className="tw-text-sm tw-mt-1">{y.label}</div></button>;
        })}</div>;
      $[11] = onSelectYear;
      $[12] = onZoomToYear;
      $[13] = startYear;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    return t2;
  }
}

/**
 * PeriodView - epochs only, one per row, with ranges; drill into Epoch view.
 */
function PeriodView(t0) {
  const $ = _c(14);
  const {
    seasonIndex,
    onSelectEpoch,
    onZoomToEpoch
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getSeasonIndexForDate(new Date());
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const currentIdx = t1;
  const periodNumber = displayedPeriodNumberFromIndex(seasonIndex);
  if (periodNumber === 0) {
    const isCurrent = currentIdx === SZN1_SEASON_INDEX;
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        borderColor: isCurrent ? "#20fa59" : "#222222",
        borderWidth: isCurrent ? "2px" : "1px"
      };
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== onSelectEpoch || $[3] !== onZoomToEpoch) {
      t3 = () => {
        onSelectEpoch(0);
        onZoomToEpoch();
      };
      $[2] = onSelectEpoch;
      $[3] = onZoomToEpoch;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    let t5;
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-font-semibold">Epoch #0 (2022)</div>;
      t5 = <div className="tw-text-xs tw-text-gray-500">Jun 2022 - Dec 2022</div>;
      t6 = <div className="tw-text-sm tw-mt-1">Memes #1 - #47</div>;
      $[5] = t4;
      $[6] = t5;
      $[7] = t6;
    } else {
      t4 = $[5];
      t5 = $[6];
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t3) {
      t7 = <div className="tw-grid tw-grid-cols-1 tw-gap-4 tw-mt-4"><button type="button" key={SZN1_SEASON_INDEX} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={t2} onClick={t3}>{t4}{t5}{t6}</button></div>;
      $[8] = t3;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  } else {
    const firstEpochNumber = 1 + 5 * (periodNumber - 1);
    let t2;
    if ($[10] !== firstEpochNumber || $[11] !== onSelectEpoch || $[12] !== onZoomToEpoch) {
      const epochs = Array.from({
        length: 5
      }, (_, k) => {
        const epochNumber = firstEpochNumber + k;
        const startYear = 2023 + 4 * (epochNumber - 1);
        const start = new Date(Date.UTC(startYear, 0, 1));
        const end = new Date(Date.UTC(startYear + 4, 0, 0));
        const seasonIndexForEpoch = getSeasonIndexForDate(new Date(Date.UTC(startYear, 0, 1)));
        return {
          epochNumber,
          start,
          end,
          seasonIndex: seasonIndexForEpoch,
          label: getRangeLabel(start, end)
        };
      });
      t2 = <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mt-4">{epochs.map(ep => {
          const isCurrent_0 = currentIdx >= ep.seasonIndex && currentIdx < ep.seasonIndex + SEASONS_PER_EPOCH;
          return <button type="button" key={toISO(ep.start)} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={{
            borderColor: isCurrent_0 ? "#20fa59" : "#222222",
            borderWidth: isCurrent_0 ? "2px" : "1px"
          }} onClick={() => {
            onSelectEpoch(ep.epochNumber);
            onZoomToEpoch();
          }}><div className="tw-font-semibold">Epoch #{ep.epochNumber} ({ep.start.getUTCFullYear()})</div><div className="tw-text-xs tw-text-gray-500">{ep.start.toLocaleString("default", {
                month: "short"
              })}{" "}{ep.start.getUTCFullYear()} -{" "}{ep.end.toLocaleString("default", {
                month: "short"
              })}{" "}{ep.end.getUTCFullYear()}</div><div className="tw-text-sm tw-mt-1">{ep.label}</div></button>;
        })}</div>;
      $[10] = firstEpochNumber;
      $[11] = onSelectEpoch;
      $[12] = onZoomToEpoch;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    return t2;
  }
}

/**
 * EraView - periods only, one per row, with ranges; drill into Period view.
 */
function EraView(t0) {
  const $ = _c(15);
  const {
    seasonIndex,
    onSelectPeriod,
    onZoomToPeriod
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getSeasonIndexForDate(new Date());
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const currentIdx = t1;
  const eraNumber = displayedEraNumberFromIndex(seasonIndex);
  if (eraNumber === 0) {
    const isCurrent = currentIdx === SZN1_SEASON_INDEX;
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        borderColor: isCurrent ? "#20fa59" : "#222222",
        borderWidth: isCurrent ? "2px" : "1px"
      };
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== onSelectPeriod || $[3] !== onZoomToPeriod) {
      t3 = () => {
        onSelectPeriod(0);
        onZoomToPeriod();
      };
      $[2] = onSelectPeriod;
      $[3] = onZoomToPeriod;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    let t5;
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-font-semibold">Period #0 (2022)</div>;
      t5 = <div className="tw-text-xs tw-text-gray-500">Jun 2022 - Dec 2022</div>;
      t6 = <div className="tw-text-sm tw-mt-1">Memes #1 - #47</div>;
      $[5] = t4;
      $[6] = t5;
      $[7] = t6;
    } else {
      t4 = $[5];
      t5 = $[6];
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t3) {
      t7 = <div className="tw-grid tw-grid-cols-1 tw-gap-4 tw-mt-4"><button type="button" key={SZN1_SEASON_INDEX} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={t2} onClick={t3}>{t4}{t5}{t6}</button></div>;
      $[8] = t3;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  const startYear = 2023 + 100 * (eraNumber - 1);
  const firstPeriodNumber = 1 + 5 * (eraNumber - 1);
  let t2;
  if ($[10] !== firstPeriodNumber || $[11] !== onSelectPeriod || $[12] !== onZoomToPeriod || $[13] !== startYear) {
    const periods = Array.from({
      length: 5
    }, (_, k) => {
      const py = startYear + 20 * k;
      const start = new Date(Date.UTC(py, 0, 1));
      const end = new Date(Date.UTC(py + 20, 0, 0));
      const seasonIndexForPeriod = getSeasonIndexForDate(start);
      return {
        periodNumber: firstPeriodNumber + k,
        start,
        end,
        seasonIndex: seasonIndexForPeriod,
        label: getRangeLabel(start, end)
      };
    });
    t2 = <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mt-4">{periods.map(p => {
        const isCurrent_0 = currentIdx >= p.seasonIndex && currentIdx < p.seasonIndex + SEASONS_PER_PERIOD;
        return <button type="button" key={toISO(p.start)} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={{
          borderColor: isCurrent_0 ? "#20fa59" : "#222222",
          borderWidth: isCurrent_0 ? "2px" : "1px"
        }} onClick={() => {
          onSelectPeriod(p.periodNumber);
          onZoomToPeriod();
        }}><div className="tw-font-semibold">Period #{p.periodNumber} ({p.start.getUTCFullYear()})</div><div className="tw-text-xs tw-text-gray-500">{p.start.toLocaleString("default", {
              month: "short"
            })}{" "}{p.start.getUTCFullYear()} -{" "}{p.end.toLocaleString("default", {
              month: "short"
            })}{" "}{p.end.getUTCFullYear()}</div><div className="tw-text-sm tw-mt-1">{p.label}</div></button>;
      })}</div>;
    $[10] = firstPeriodNumber;
    $[11] = onSelectPeriod;
    $[12] = onZoomToPeriod;
    $[13] = startYear;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}

/**
 * EonView - eras only, one per row, with ranges; drill into Era view.
 */
function EonView(t0) {
  const $ = _c(15);
  const {
    seasonIndex,
    onSelectEra,
    onZoomToEra
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getSeasonIndexForDate(new Date());
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const currentIdx = t1;
  const eonNumber = displayedEonNumberFromIndex(seasonIndex);
  if (eonNumber === 0) {
    const isCurrent = currentIdx === SZN1_SEASON_INDEX;
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        borderColor: isCurrent ? "#20fa59" : "#222222",
        borderWidth: isCurrent ? "2px" : "1px"
      };
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== onSelectEra || $[3] !== onZoomToEra) {
      t3 = () => {
        onSelectEra(0);
        onZoomToEra();
      };
      $[2] = onSelectEra;
      $[3] = onZoomToEra;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    let t5;
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-font-semibold">Era #0 (2022)</div>;
      t5 = <div className="tw-text-xs tw-text-gray-500">Jun 2022 - Dec 2022</div>;
      t6 = <div className="tw-text-sm tw-mt-1">Memes #1 - #47</div>;
      $[5] = t4;
      $[6] = t5;
      $[7] = t6;
    } else {
      t4 = $[5];
      t5 = $[6];
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t3) {
      t7 = <div className="tw-grid tw-grid-cols-1 tw-gap-4 tw-mt-4"><button type="button" key={SZN1_SEASON_INDEX} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={t2} onClick={t3}>{t4}{t5}{t6}</button></div>;
      $[8] = t3;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  const startYear = 2023 + 1000 * (eonNumber - 1);
  const firstEraNumber = 1 + 10 * (eonNumber - 1);
  let t2;
  if ($[10] !== firstEraNumber || $[11] !== onSelectEra || $[12] !== onZoomToEra || $[13] !== startYear) {
    const eras = Array.from({
      length: 10
    }, (_, k) => {
      const ey = startYear + 100 * k;
      const start = new Date(Date.UTC(ey, 0, 1));
      const end = new Date(Date.UTC(ey + 100, 0, 0));
      const seasonIndexForEra = getSeasonIndexForDate(start);
      return {
        eraNumber: firstEraNumber + k,
        start,
        end,
        seasonIndex: seasonIndexForEra,
        label: getRangeLabel(start, end)
      };
    });
    t2 = <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mt-4">{eras.map(er => {
        const isCurrent_0 = currentIdx >= er.seasonIndex && currentIdx < er.seasonIndex + SEASONS_PER_ERA;
        return <button type="button" key={toISO(er.start)} className="tw-p-3 tw-cursor-pointer tw-bg-black tw-rounded-md tw-border tw-border-solid tw-border-[#222222] hover:tw-bg-[#eee] hover:tw-text-black" style={{
          borderColor: isCurrent_0 ? "#20fa59" : "#222222",
          borderWidth: isCurrent_0 ? "2px" : "1px"
        }} onClick={() => {
          onSelectEra(er.eraNumber);
          onZoomToEra();
        }}><div className="tw-font-semibold">Era #{er.eraNumber} ({er.start.getUTCFullYear()})</div><div className="tw-text-xs tw-text-gray-500">{er.start.toLocaleString("default", {
              month: "short"
            })}{" "}{er.start.getUTCFullYear()} -{" "}{er.end.toLocaleString("default", {
              month: "short"
            })}{" "}{er.end.getUTCFullYear()}</div><div className="tw-text-sm tw-mt-1">{er.label}</div></button>;
      })}</div>;
    $[10] = firstEraNumber;
    $[11] = onSelectEra;
    $[12] = onZoomToEra;
    $[13] = startYear;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}

/**
 * Main MemeCalendar component (TypeScript). Manages state and renders
 * appropriate views based on zoom level.
 */
interface MemeCalendarProps {
  readonly displayTz: DisplayTz;
}
export default function MemeCalendar(t0) {
  const $ = _c(155);
  const {
    displayTz
  } = t0;
  const isMobile = useIsMobileScreen();
  const [seasonIndex, setSeasonIndex] = useState(_temp4);
  const [zoomLevel, setZoomLevel] = useState("szn");
  const [jumpValue, setJumpValue] = useState("");
  const [jumpMint, setJumpMint] = useState("");
  const [autoOpenYmd, setAutoOpenYmd] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  let clampIndex;
  let eonStartIndex;
  let epochStartIndex;
  let eraStartIndex;
  let handleDateJumpSubmit;
  let handleJumpToToday;
  let handleMintJumpSubmit;
  let jumpToMonthValue;
  let periodStartIndex;
  let renderView;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== autoOpenYmd || $[1] !== displayTz || $[2] !== jumpMint || $[3] !== jumpValue || $[4] !== seasonIndex || $[5] !== zoomLevel) {
    const seasonNumber = displayedSeasonNumberFromIndex(seasonIndex);
    const yearNumber = displayedYearNumberFromIndex(seasonIndex);
    const epochNumber = displayedEpochNumberFromIndex(seasonIndex);
    const periodNumber = displayedPeriodNumberFromIndex(seasonIndex);
    const eraNumber = displayedEraNumberFromIndex(seasonIndex);
    const eonNumber = displayedEonNumberFromIndex(seasonIndex);
    if ($[25] !== autoOpenYmd || $[26] !== displayTz || $[27] !== seasonIndex || $[28] !== zoomLevel) {
      const selectYear = n => setSeasonIndex(clampIndex((n - 4) * SEASONS_PER_YEAR));
      const selectEpoch = n_0 => setSeasonIndex(clampIndex((4 * n_0 - 7) * SEASONS_PER_YEAR));
      const selectPeriod = n_1 => setSeasonIndex(clampIndex(80 * (n_1 - 1) - 12));
      const selectEra = n_2 => setSeasonIndex(clampIndex(400 * (n_2 - 1) - 12));
      renderView = () => {
        switch (zoomLevel) {
          case "szn":
            {
              return <SeasonView seasonIndex={seasonIndex} autoOpenYmd={autoOpenYmd ?? undefined} displayTz={displayTz} />;
            }
          case "year":
            {
              return <YearView seasonIndex={seasonIndex} onSelectSeason={setSeasonIndex} onZoomToSeason={() => setZoomLevel("szn")} />;
            }
          case "epoch":
            {
              return <EpochView seasonIndex={seasonIndex} onSelectSeason={setSeasonIndex} onSelectYear={selectYear} onZoomToYear={() => setZoomLevel("year")} />;
            }
          case "period":
            {
              return <PeriodView seasonIndex={seasonIndex} onSelectEpoch={selectEpoch} onZoomToEpoch={() => setZoomLevel("epoch")} />;
            }
          case "era":
            {
              return <EraView seasonIndex={seasonIndex} onSelectPeriod={selectPeriod} onZoomToPeriod={() => setZoomLevel("period")} />;
            }
          case "eon":
            {
              return <EonView seasonIndex={seasonIndex} onSelectEra={selectEra} onZoomToEra={() => setZoomLevel("era")} />;
            }
          default:
            {
              return null;
            }
        }
      };
      const MAX_SEASON_INDEX = Number.POSITIVE_INFINITY;
      clampIndex = i => Math.max(SZN1_SEASON_INDEX, Math.min(MAX_SEASON_INDEX, i));
      $[25] = autoOpenYmd;
      $[26] = displayTz;
      $[27] = seasonIndex;
      $[28] = zoomLevel;
      $[29] = clampIndex;
      $[30] = renderView;
    } else {
      clampIndex = $[29];
      renderView = $[30];
    }
    epochStartIndex = _temp5;
    periodStartIndex = _temp6;
    eraStartIndex = _temp7;
    eonStartIndex = _temp8;
    let t10;
    if ($[31] !== clampIndex) {
      t10 = () => {
        const now = new Date();
        const idx = getSeasonIndexForDate(now);
        setSeasonIndex(clampIndex(idx));
        setZoomLevel("szn");
      };
      $[31] = clampIndex;
      $[32] = t10;
    } else {
      t10 = $[32];
    }
    handleJumpToToday = t10;
    let t11;
    if ($[33] !== clampIndex || $[34] !== jumpMint) {
      t11 = () => {
        const n_7 = parseInt(jumpMint, 10);
        if (!n_7 || n_7 < 1) {
          return;
        }
        const d = dateFromMintNumber(n_7);
        const idx_0 = getSeasonIndexForDate(d);
        setSeasonIndex(clampIndex(idx_0));
        setZoomLevel("szn");
        setAutoOpenYmd(ymd(d));
        setTimeout(() => setAutoOpenYmd(null), 1200);
      };
      $[33] = clampIndex;
      $[34] = jumpMint;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    const jumpToMintNumber = t11;
    let t12;
    if ($[36] !== clampIndex) {
      t12 = value => {
        if (!value) {
          return;
        }
        const [ys, ms] = value.split("-");
        const y = Number(ys);
        const m = Number(ms);
        if (!y || !m) {
          return;
        }
        const d_0 = new Date(y, m - 1, 1);
        const idx_1 = getSeasonIndexForDate(d_0);
        setSeasonIndex(clampIndex(idx_1));
        setZoomLevel("szn");
      };
      $[36] = clampIndex;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    jumpToMonthValue = t12;
    let t13;
    if ($[38] !== jumpToMintNumber) {
      t13 = event => {
        event.preventDefault();
        jumpToMintNumber();
      };
      $[38] = jumpToMintNumber;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    handleMintJumpSubmit = t13;
    let t14;
    if ($[40] !== jumpToMonthValue || $[41] !== jumpValue) {
      t14 = event_0 => {
        event_0.preventDefault();
        jumpToMonthValue(jumpValue);
      };
      $[40] = jumpToMonthValue;
      $[41] = jumpValue;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    handleDateJumpSubmit = t14;
    t9 = "tw-p-4 tw-bg-[#0c0c0d] tw-rounded-md tw-border tw-border-solid tw-border-[#222222]";
    t8 = "tw-mb-8 tw-grid tw-grid-cols-3 lg:tw-grid-cols-[repeat(6,minmax(0,1fr))_auto] tw-gap-2";
    const t15 = `SZN ${seasonNumber.toLocaleString()}`;
    if ($[43] !== t15) {
      t3 = ["szn", t15];
      $[43] = t15;
      $[44] = t3;
    } else {
      t3 = $[44];
    }
    const t16 = `Year ${yearNumber.toLocaleString()}`;
    if ($[45] !== t16) {
      t4 = ["year", t16];
      $[45] = t16;
      $[46] = t4;
    } else {
      t4 = $[46];
    }
    const t17 = `Epoch ${epochNumber.toLocaleString()}`;
    if ($[47] !== t17) {
      t5 = ["epoch", t17];
      $[47] = t17;
      $[48] = t5;
    } else {
      t5 = $[48];
    }
    const t18 = `Period ${periodNumber.toLocaleString()}`;
    if ($[49] !== t18) {
      t6 = ["period", t18];
      $[49] = t18;
      $[50] = t6;
    } else {
      t6 = $[50];
    }
    const t19 = `Era ${eraNumber.toLocaleString()}`;
    if ($[51] !== t19) {
      t7 = ["era", t19];
      $[51] = t19;
      $[52] = t7;
    } else {
      t7 = $[52];
    }
    t2 = "eon";
    t1 = eonNumber.toLocaleString();
    $[0] = autoOpenYmd;
    $[1] = displayTz;
    $[2] = jumpMint;
    $[3] = jumpValue;
    $[4] = seasonIndex;
    $[5] = zoomLevel;
    $[6] = clampIndex;
    $[7] = eonStartIndex;
    $[8] = epochStartIndex;
    $[9] = eraStartIndex;
    $[10] = handleDateJumpSubmit;
    $[11] = handleJumpToToday;
    $[12] = handleMintJumpSubmit;
    $[13] = jumpToMonthValue;
    $[14] = periodStartIndex;
    $[15] = renderView;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    clampIndex = $[6];
    eonStartIndex = $[7];
    epochStartIndex = $[8];
    eraStartIndex = $[9];
    handleDateJumpSubmit = $[10];
    handleJumpToToday = $[11];
    handleMintJumpSubmit = $[12];
    jumpToMonthValue = $[13];
    periodStartIndex = $[14];
    renderView = $[15];
    t1 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  const t10 = `Eon ${t1}`;
  let t11;
  if ($[53] !== t10 || $[54] !== t2) {
    t11 = [t2, t10];
    $[53] = t10;
    $[54] = t2;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  let t12;
  if ($[56] !== t11 || $[57] !== t3 || $[58] !== t4 || $[59] !== t5 || $[60] !== t6 || $[61] !== t7) {
    t12 = [t3, t4, t5, t6, t7, t11];
    $[56] = t11;
    $[57] = t3;
    $[58] = t4;
    $[59] = t5;
    $[60] = t6;
    $[61] = t7;
    $[62] = t12;
  } else {
    t12 = $[62];
  }
  const t13 = t12 as [ZoomLevel, string][];
  let t14;
  if ($[63] !== t13 || $[64] !== zoomLevel) {
    t14 = t13.map(t15 => {
      const [level, label] = t15;
      return <button key={level} className={"tw-w-full tw-px-3 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-border tw-transition-colors " + (zoomLevel === level ? "tw-bg-blue-600 tw-text-white tw-border-blue-500 tw-shadow" : "tw-bg-gray-100 tw-text-gray-900 tw-border-gray-300 hover:tw-bg-gray-200 dark:tw-bg-gray-800 dark:tw-text-gray-100 dark:tw-border-gray-700 dark:hover:tw-bg-gray-700")} onClick={() => setZoomLevel(level)}>{label}</button>;
    });
    $[63] = t13;
    $[64] = zoomLevel;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  const t15 = showInfo ? faXmarkCircle : faInfoCircle;
  let t16;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setShowInfo(_temp9);
    $[66] = t16;
  } else {
    t16 = $[66];
  }
  let t17;
  if ($[67] !== t15) {
    t17 = <div className="tw-col-span-3 lg:tw-col-span-1 tw-flex tw-items-center tw-justify-end"><FontAwesomeIcon icon={t15} className="tw-w-8 tw-h-8 tw-cursor-pointer" onClick={t16} /></div>;
    $[67] = t15;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  let t18;
  if ($[69] !== t14 || $[70] !== t17 || $[71] !== t8) {
    t18 = <div className={t8}>{t14}{t17}</div>;
    $[69] = t14;
    $[70] = t17;
    $[71] = t8;
    $[72] = t18;
  } else {
    t18 = $[72];
  }
  const t19 = "tw-rounded-md tw-bg-black tw-border tw-border-solid tw-border-[#222222] tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-out tw-origin-top " + (showInfo ? "tw-mb-8 tw-opacity-100 tw-scale-y-100 tw-py-5 tw-px-3" : "tw-opacity-0 tw-max-h-0 tw-scale-y-95 tw-pointer-events-none");
  const t20 = !showInfo;
  let t21;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 md:tw-gap-6"><div className="md:tw-flex-1">{[{
          label: "Minting Days",
          text: "Monday / Wednesday / Friday"
        }, {
          label: "SZN",
          text: "Traditional calendar quarter system / 3 months each",
          note: "~ 39 mints"
        }, {
          label: "YEAR",
          text: "4 SZNs in 1 YEAR",
          note: "~ 156 mints"
        }, {
          label: "EPOCH",
          text: "4 YEARs / 16 SZNs",
          note: "~ 626 mints"
        }, {
          label: "PERIOD",
          text: "5 EPOCHs / 20 YEARs / 80 SZNs",
          note: "~ 3,130 mints"
        }, {
          label: "ERA",
          text: "5 PERIODs / 20 EPOCHs / 100 YEARs / 400 SZNs",
          note: "~ 15,650 mints"
        }, {
          label: "EON",
          text: "10 ERAs / 100 PERIODs / 1,000 YEARs / 4,000 SZNs",
          note: "~ 156,500 mints"
        }, {
          label: "Year 0",
          text: "Jun 2022 - Dec 2022 / SZN1 / Year 0 was our experimental launch period, not bound by the later structured minting schedule.",
          note: "Memes #1 - #47"
        }].map(_temp10)}</div></div>;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== t19 || $[75] !== t20) {
    t22 = <div className={t19} aria-hidden={t20}>{t21}</div>;
    $[74] = t19;
    $[75] = t20;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== clampIndex || $[78] !== eonStartIndex || $[79] !== epochStartIndex || $[80] !== eraStartIndex || $[81] !== periodStartIndex || $[82] !== seasonIndex || $[83] !== zoomLevel) {
    t23 = () => {
      let delta = 0;
      if (zoomLevel === "epoch") {
        const en = displayedEpochNumberFromIndex(seasonIndex);
        setSeasonIndex(clampIndex(epochStartIndex(en - 1)));
        return;
      }
      if (zoomLevel === "period") {
        const pn = displayedPeriodNumberFromIndex(seasonIndex);
        setSeasonIndex(clampIndex(periodStartIndex(pn - 1)));
        return;
      }
      if (zoomLevel === "era") {
        const rn = displayedEraNumberFromIndex(seasonIndex);
        setSeasonIndex(clampIndex(eraStartIndex(rn - 1)));
        return;
      }
      if (zoomLevel === "eon") {
        const en_0 = displayedEonNumberFromIndex(seasonIndex);
        setSeasonIndex(clampIndex(eonStartIndex(en_0 - 1)));
        return;
      }
      bb138: switch (zoomLevel) {
        case "szn":
          {
            delta = -1;
            break bb138;
          }
        case "year":
          {
            delta = -SEASONS_PER_YEAR;
          }
      }
      setSeasonIndex(s => clampIndex(s + delta));
    };
    $[77] = clampIndex;
    $[78] = eonStartIndex;
    $[79] = epochStartIndex;
    $[80] = eraStartIndex;
    $[81] = periodStartIndex;
    $[82] = seasonIndex;
    $[83] = zoomLevel;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  let t24;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <FontAwesomeIcon icon={faCaretLeft} />;
    $[85] = t24;
  } else {
    t24 = $[85];
  }
  let t25;
  if ($[86] !== t23) {
    t25 = <button className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-3 tw-py-1.5 tw-w-9 tw-h-9 tw-rounded-md tw-border tw-border-gray-300 hover:tw-bg-gray-100 dark:tw-border-gray-700 dark:hover:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-gray-100" onClick={t23}>{t24}</button>;
    $[86] = t23;
    $[87] = t25;
  } else {
    t25 = $[87];
  }
  let t26;
  if ($[88] !== seasonIndex || $[89] !== zoomLevel) {
    t26 = getZoomTitle(zoomLevel, seasonIndex);
    $[88] = seasonIndex;
    $[89] = zoomLevel;
    $[90] = t26;
  } else {
    t26 = $[90];
  }
  let t27;
  if ($[91] !== t26) {
    t27 = <div className="tw-font-semibold tw-text-sm">{t26}</div>;
    $[91] = t26;
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  let range;
  let t28;
  if ($[93] !== seasonIndex || $[94] !== zoomLevel) {
    const {
      start,
      end
    } = getRangeDatesByZoom(zoomLevel, seasonIndex);
    range = `${formatMonthYearShort(start)} - ${formatMonthYearShort(end)}`;
    t28 = isSznOneIndex(seasonIndex) ? "Memes #1 - #47" : getRangeLabel(start, end);
    $[93] = seasonIndex;
    $[94] = zoomLevel;
    $[95] = range;
    $[96] = t28;
  } else {
    range = $[95];
    t28 = $[96];
  }
  const mintRange = t28;
  let t29;
  if ($[97] !== mintRange || $[98] !== range) {
    t29 = <div className="tw-text-xs tw-text-gray-400 tw-whitespace-normal tw-break-words">{range} / {mintRange}</div>;
    $[97] = mintRange;
    $[98] = range;
    $[99] = t29;
  } else {
    t29 = $[99];
  }
  const t30 = t29;
  let t31;
  if ($[100] !== t27 || $[101] !== t30) {
    t31 = <div className="tw-flex-1 tw-min-w-0 tw-text-center">{t27}{t30}</div>;
    $[100] = t27;
    $[101] = t30;
    $[102] = t31;
  } else {
    t31 = $[102];
  }
  let t32;
  if ($[103] !== clampIndex || $[104] !== eonStartIndex || $[105] !== epochStartIndex || $[106] !== eraStartIndex || $[107] !== periodStartIndex || $[108] !== seasonIndex || $[109] !== zoomLevel) {
    t32 = () => {
      let delta_0 = 0;
      if (zoomLevel === "epoch") {
        const en_1 = displayedEpochNumberFromIndex(seasonIndex);
        setSeasonIndex(clampIndex(epochStartIndex(en_1 + 1)));
        return;
      }
      if (zoomLevel === "period") {
        const pn_0 = displayedPeriodNumberFromIndex(seasonIndex);
        setSeasonIndex(clampIndex(periodStartIndex(pn_0 + 1)));
        return;
      }
      if (zoomLevel === "era") {
        const rn_0 = displayedEraNumberFromIndex(seasonIndex);
        setSeasonIndex(clampIndex(eraStartIndex(rn_0 + 1)));
        return;
      }
      if (zoomLevel === "eon") {
        const en_2 = displayedEonNumberFromIndex(seasonIndex);
        setSeasonIndex(clampIndex(eonStartIndex(en_2 + 1)));
        return;
      }
      bb164: switch (zoomLevel) {
        case "szn":
          {
            delta_0 = 1;
            break bb164;
          }
        case "year":
          {
            delta_0 = SEASONS_PER_YEAR;
          }
      }
      setSeasonIndex(s_0 => clampIndex(s_0 + delta_0));
    };
    $[103] = clampIndex;
    $[104] = eonStartIndex;
    $[105] = epochStartIndex;
    $[106] = eraStartIndex;
    $[107] = periodStartIndex;
    $[108] = seasonIndex;
    $[109] = zoomLevel;
    $[110] = t32;
  } else {
    t32 = $[110];
  }
  let t33;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <FontAwesomeIcon icon={faCaretRight} />;
    $[111] = t33;
  } else {
    t33 = $[111];
  }
  let t34;
  if ($[112] !== t32) {
    t34 = <button className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-3 tw-py-1.5 tw-w-9 tw-h-9 tw-rounded-md tw-border tw-border-gray-300 hover:tw-bg-gray-100 dark:tw-border-gray-700 dark:hover:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-gray-100" onClick={t32}>{t33}</button>;
    $[112] = t32;
    $[113] = t34;
  } else {
    t34 = $[113];
  }
  let t35;
  if ($[114] !== t25 || $[115] !== t31 || $[116] !== t34) {
    t35 = <div className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0 lg:tw-basis-2/3 lg:tw-max-w-[40%]">{t25}{t31}{t34}</div>;
    $[114] = t25;
    $[115] = t31;
    $[116] = t34;
    $[117] = t35;
  } else {
    t35 = $[117];
  }
  let t36;
  if ($[118] !== handleJumpToToday) {
    t36 = <button type="button" className="tw-whitespace-nowrap tw-inline-flex tw-items-center tw-justify-center tw-h-9 tw-rounded-md tw-bg-white tw-text-black tw-px-3 tw-text-sm tw-font-semibold hover:tw-bg-[#e9e9e9] tw-border tw-border-[#d1d1d1] tw-shrink-0 tw-flex-1 sm:tw-flex-none sm:tw-w-auto" onClick={handleJumpToToday}>Jump to Today</button>;
    $[118] = handleJumpToToday;
    $[119] = t36;
  } else {
    t36 = $[119];
  }
  let t37;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="tw-shrink-0 tw-select-none tw-pr-2">Meme #</div>;
    $[120] = t37;
  } else {
    t37 = $[120];
  }
  let t38;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className="tw-bg-[#e5e5e5] tw-h-9 tw-flex tw-items-center tw-rounded-md tw-bg-white tw-text-black tw-font-semibold tw-pl-3 tw-border tw-border-[#d1d1d1] tw-w-full">{t37}<input id="meme-calendar-mint-input" type="number" min={1} name="meme-calendar-mint-input" placeholder="123" onChange={event_1 => {
        const v_0 = event_1.target.value.replaceAll(/\D/g, "");
        setJumpMint(v_0);
      }} className="tw-text-black placeholder:tw-text-gray-500 focus:tw-outline-none tw-border-none tw-h-9 tw-w-full sm:tw-w-[8ch] tw-min-w-0 tw-px-2 tw-rounded-r-md" /></div>;
    $[121] = t38;
  } else {
    t38 = $[121];
  }
  let t39;
  if ($[122] !== handleMintJumpSubmit) {
    t39 = <form onSubmit={handleMintJumpSubmit} className="tw-shrink-0 tw-flex-1 tw-w-full sm:tw-flex-none sm:tw-w-auto">{t38}</form>;
    $[122] = handleMintJumpSubmit;
    $[123] = t39;
  } else {
    t39 = $[123];
  }
  let t40;
  if ($[124] !== t36 || $[125] !== t39) {
    t40 = <div className="tw-flex tw-w-full tw-gap-3 sm:tw-w-auto sm:tw-gap-3">{t36}{t39}</div>;
    $[124] = t36;
    $[125] = t39;
    $[126] = t40;
  } else {
    t40 = $[126];
  }
  let t41;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="tw-shrink-0 tw-select-none tw-pr-2">Date</div>;
    $[127] = t41;
  } else {
    t41 = $[127];
  }
  let t42;
  if ($[128] !== jumpToMonthValue) {
    t42 = event_2 => {
      const value_0 = event_2.target.value;
      setJumpValue(value_0);
      jumpToMonthValue(value_0);
    };
    $[128] = jumpToMonthValue;
    $[129] = t42;
  } else {
    t42 = $[129];
  }
  let t43;
  if ($[130] !== jumpValue || $[131] !== t42) {
    t43 = <div className="tw-bg-[#e5e5e5] tw-h-9 tw-flex tw-items-center tw-rounded-md tw-bg-white tw-text-black tw-font-semibold tw-pl-3 tw-border tw-border-[#d1d1d1] tw-w-full sm:tw-w-auto lg:tw-w-full tw-max-w-full sm:tw-max-w-[28rem]">{t41}<input id="meme-calendar-date-input" type="month" value={jumpValue} onChange={t42} className="tw-text-black placeholder:tw-text-gray-500 focus:tw-outline-none tw-border-none tw-h-9 tw-px-2 tw-rounded-r-md tw-w-full tw-min-w-0 sm:tw-w-[16rem] lg:tw-w-full lg:tw-max-w-[28rem]" /></div>;
    $[130] = jumpValue;
    $[131] = t42;
    $[132] = t43;
  } else {
    t43 = $[132];
  }
  let t44;
  if ($[133] !== handleDateJumpSubmit || $[134] !== t43) {
    t44 = <form onSubmit={handleDateJumpSubmit} className="tw-hidden sm:tw-block sm:tw-basis-auto sm:tw-w-auto lg:tw-flex-1 tw-min-w-0 tw-max-w-full">{t43}</form>;
    $[133] = handleDateJumpSubmit;
    $[134] = t43;
    $[135] = t44;
  } else {
    t44 = $[135];
  }
  let t45;
  if ($[136] !== t40 || $[137] !== t44) {
    t45 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-w-full tw-justify-center lg:tw-w-auto lg:tw-justify-end">{t40}{t44}</div>;
    $[136] = t40;
    $[137] = t44;
    $[138] = t45;
  } else {
    t45 = $[138];
  }
  let t46;
  if ($[139] !== t35 || $[140] !== t45) {
    t46 = <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3 tw-mt-2">{t35}{t45}</div>;
    $[139] = t35;
    $[140] = t45;
    $[141] = t46;
  } else {
    t46 = $[141];
  }
  let t47;
  if ($[142] !== renderView) {
    t47 = renderView();
    $[142] = renderView;
    $[143] = t47;
  } else {
    t47 = $[143];
  }
  let t48;
  if ($[144] !== t47) {
    t48 = <div>{t47}</div>;
    $[144] = t47;
    $[145] = t48;
  } else {
    t48 = $[145];
  }
  const t49 = `!tw-opacity-[0.975] !tw-text-black !tw-rounded-md !tw-border !tw-border-solid !tw-border-[#222222] !tw-z-[1000] !tw-whitespace-normal ${isMobile ? "tw-max-w-[15rem]" : "tw-max-w-[22rem] "}`;
  let t50;
  if ($[146] !== t49) {
    t50 = <Tooltip id="meme-tooltip" clickable={true} openOnClick={true} className={t49} />;
    $[146] = t49;
    $[147] = t50;
  } else {
    t50 = $[147];
  }
  let t51;
  if ($[148] !== t18 || $[149] !== t22 || $[150] !== t46 || $[151] !== t48 || $[152] !== t50 || $[153] !== t9) {
    t51 = <div className={t9}>{t18}{t22}{t46}{t48}{t50}</div>;
    $[148] = t18;
    $[149] = t22;
    $[150] = t46;
    $[151] = t48;
    $[152] = t50;
    $[153] = t9;
    $[154] = t51;
  } else {
    t51 = $[154];
  }
  return t51;
}
function _temp10(t0) {
  const {
    label: label_0,
    text,
    note
  } = t0;
  return <div key={label_0} className="tw-py-2"><span className="tw-font-bold">{label_0}</span> - {text}{" "}{note && <span className="tw-text-gray-500">{note}</span>}</div>;
}
function _temp9(v) {
  return !v;
}
function _temp8(n_6) {
  return n_6 === 0 ? SZN1_SEASON_INDEX : getSeasonIndexForDate(new Date(Date.UTC(2023 + 1000 * (n_6 - 1), 0, 1)));
}
function _temp7(n_5) {
  return n_5 === 0 ? SZN1_SEASON_INDEX : getSeasonIndexForDate(new Date(Date.UTC(2023 + 100 * (n_5 - 1), 0, 1)));
}
function _temp6(n_4) {
  return n_4 === 0 ? SZN1_SEASON_INDEX : getSeasonIndexForDate(new Date(Date.UTC(2023 + 20 * (n_4 - 1), 0, 1)));
}
function _temp5(n_3) {
  return n_3 === 0 ? SZN1_SEASON_INDEX : getSeasonIndexForDate(new Date(Date.UTC(2023 + 4 * (n_3 - 1), 0, 1)));
}
function _temp4() {
  try {
    return getSeasonIndexForDate(new Date());
  } catch {
    return 0;
  }
}